import { useLocation, useNavigate } from "react-router-dom";
import { Settings, UserCircle2, ClipboardCheck, MapPin, ExternalLink, Columns2, ArrowLeftRight, X } from "lucide-react";
import { NAV_GROUPS } from "@/components/Sidebar";
import { GlobalSearchInline } from "@/components/CommandPalette";
import { DotseroButton } from "@/components/DotseroButton";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth/AuthContext";
import { useSplit, toggleSplit, closeSplit, swapPanes } from "@/lib/splitScreen";

// Routes that are not sidebar items but still get a header. Checked after the nav items; longest prefix wins.
const EXTRA = [
  { prefix: "/settings", label: "Settings", icon: Settings },
  { prefix: "/profile", label: "My Profile", icon: UserCircle2 },
  { prefix: "/inspections", label: "Inspection", icon: ClipboardCheck },
  { prefix: "/sites", label: "Sites", icon: MapPin },
];

export function resolvePage(pathname) {
  const path = pathname === "/" ? "/" : pathname.replace(/\/$/, "");
  const items = NAV_GROUPS.flatMap((g) => g.items);
  const exact = items.find((i) => i.path === path);
  if (exact) return exact;
  const byPrefix = items.filter((i) => i.path !== "/" && path.startsWith(`${i.path}/`)).sort((a, b) => b.path.length - a.path.length)[0];
  if (byPrefix) return byPrefix;
  return EXTRA.find((e) => path === e.prefix || path.startsWith(`${e.prefix}/`)) || null;
}

const iconBtn = "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/10 text-slate-400 transition-colors hover:border-white/25 hover:text-slate-100";

// 64px dark page header — icon + page name on the left, global Quick Search (⌘K) on the right. Desktop only.
// Split screen: the right pane's title + swap/close sit at the pane boundary so each half is labelled.
export default function PageHeader({ onOpenPalette }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const split = useSplit();
  const page = resolvePage(pathname);
  if (!page) return null;
  const Icon = page.icon;
  const home = page.path || page.prefix || "/";
  const panePage = split.on && split.paneLocation ? resolvePage(split.paneLocation.pathname) : null;
  // Same tab on both sides → one title, no pane controls; the highlighted split button closes it.
  const dual = !!panePage && (panePage.path || panePage.prefix) !== (page.path || page.prefix);
  const PaneIcon = panePage?.icon;
  // Header has 16px side padding; the panes below split the full width → left share = ratio% of the padded box + ratio*0.32 − 16 px.
  const leftShare = `calc(${split.ratio}% + ${(Number(split.ratio) * 0.32 - 16).toFixed(1)}px)`;
  return (
    <header className="abs-page-header hidden md:flex h-16 shrink-0 items-center px-4" data-testid="page-header" data-split-header={dual ? "dual" : "single"}>
      <div className="flex min-w-0 items-center" style={dual ? { flex: `0 0 ${leftShare}` } : undefined}>
        {/* Icon + name = "take me to this section's main view" (clears tabs, filters and deep links). */}
        <button type="button" onClick={() => navigate(home)} title={t("Back to {page}", { page: t(page.label) })} data-testid="page-header-home"
          className="inline-flex min-w-0 items-center gap-2.5 rounded-md px-1 -mx-1 hover:bg-white/5 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/40">
          {Icon && <Icon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />}
          <h1 className="truncate text-lg sm:text-xl font-semibold tracking-tight text-white" style={{ fontFamily: "var(--font-heading)" }} data-testid="page-header-title">
            {t(page.title || page.label)}
          </h1>
        </button>
      </div>
      {dual && (
        <div className="flex min-w-0 flex-1 items-center gap-1 border-l border-white/10 pl-3" data-testid="split-pane-header">
          {PaneIcon && <PaneIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />}
          <h2 className="truncate text-lg sm:text-xl font-semibold tracking-tight text-white" style={{ fontFamily: "var(--font-heading)" }} data-testid="split-pane-title">
            {panePage ? t(panePage.title || panePage.label) : "—"}
          </h2>
          <button type="button" onClick={swapPanes} title={t("Swap panes")} aria-label={t("Swap panes")} className={`${iconBtn} ml-1 !h-7 !w-7`} data-testid="split-swap-button">
            <ArrowLeftRight className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={closeSplit} title={t("Close split screen")} aria-label={t("Close split screen")} className={`${iconBtn} !h-7 !w-7`} data-testid="split-close-button">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {onOpenPalette && <GlobalSearchInline />}
      <button type="button" onClick={() => toggleSplit(user?.split_start || "same")}
        title={split.on ? t("Close split screen") : t("Split screen")} aria-label={t("Split screen")} aria-pressed={split.on}
        className={`mr-2 ${iconBtn} ${split.on ? "!border-sky-400/60 !text-sky-300 bg-sky-500/10" : ""}`}
        data-testid="split-screen-button">
        <Columns2 className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => window.open(window.location.href, "_blank", "noopener")}
        title={t("Open this page in a new tab")} aria-label={t("Open this page in a new tab")}
        className={`mr-2 ${iconBtn}`}
        data-testid="open-new-tab-button">
        <ExternalLink className="h-4 w-4" />
      </button>
      <DotseroButton />
    </header>
  );
}
